import type { SkillRoute } from '@elara/domain/schemas/skill-route-schema';

/**
 * One plain line per injection moment, for a reply's Debug Info:
 * `step 0 · DEST_REVIEW · destinations, dining (intent .84)` for the turn start, then
 * `step 2 · → HOTEL_REQS · +hotels −destinations` for each mid-turn state move.
 * A skill with a Jev score came in by intent; any other skill owns the state shown before it.
 */
export function describeSkillRouteLines(route: SkillRoute): string[] {
  const label = (name: string): string => {
    const probability = route.probabilities?.[name];
    if (probability !== undefined) return `${name} (intent ${probability.toFixed(2).replace(/^0/, '')})`;
    return route.state && route.reason !== 'intent' ? name : `${name} (intent)`;
  };
  const start = route.skills.map(label).join(', ');
  const lines = [`step 0 · ${route.state ?? 'no state'} · ${start || 'no playbook'}`];

  let previous = route.skills;
  for (const move of route.transitions ?? []) {
    const added = move.skills.filter((name) => !previous.includes(name)).map((name) => `+${name}`);
    const dropped = previous.filter((name) => !move.skills.includes(name)).map((name) => `−${name}`);
    const step = move.step !== undefined ? `step ${move.step} · ` : '';
    lines.push(`${step}→ ${move.to} · ${[...added, ...dropped].join(' ') || 'same playbooks'}`);
    previous = move.skills;
  }
  return lines;
}

/** Skills in the prompt after the last recorded move, else the turn-start set. */
export function skillsInPrompt(route: SkillRoute): string[] {
  return route.transitions?.at(-1)?.skills ?? route.skills;
}
